import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChicagoFunctions } from '@functions/chicago.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import LogRocket from 'logrocket';
import { ViewSelectSnapshot } from '@ngxs-labs/select-snapshot';
import { ICON } from '@core/util/icon';
import {
	MatterManualTitleInsurance,
	TitleInsuranceCancellationOptions,
} from '@models/title-insurance';
import { NgForm } from '@angular/forms';
import { ChicagoStatus } from '@core/util/chicago';

export interface CancelModalData {
	status: number;
}

@UntilDestroy()
@Component({
	selector: 'app-cancel-chicago-modal',
	templateUrl: './cancel-modal.component.html',
	standalone: false,
})
export class CancelModalComponent implements OnInit {
	public titleInsurance: MatterManualTitleInsurance;
	public status;
	public confirm = false;
	public submitted = false;

	public cancelOptions = [
		{
			items: TitleInsuranceCancellationOptions,
		},
	];

	public cancelCode = 0;
	public cancelValue = TitleInsuranceCancellationOptions[0].name;
	public cancelText = '';
	public ICON = ICON;
	public loading = false;
	public canCancel = true;

	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterManualTitleInsurance>;
	@ViewSelectSnapshot(SelectedMatter.closingDate) closingDate: string;

	constructor(
		private dialogRef: MatDialogRef<CancelModalComponent>,
		@Inject(MAT_DIALOG_DATA) public _data: CancelModalData,
		private chicagoFunction: ChicagoFunctions,
	) {}

	ngOnInit(): void {
		this.status = this._data.status;
		this.titleInsurance$
			.pipe(untilDestroyed(this))
			.subscribe((_titleInsurance) => {
				if (_titleInsurance) {
					this.titleInsurance = _titleInsurance;
				}
			});
		//check if closing date has passed
		if (this.closingDate) {
			const closingDate = new Date(this.closingDate);
			const today = new Date();
			if (
				closingDate < today &&
				this.status === ChicagoStatus.READY_FOR_PICKUP
			) {
				this.canCancel = false;
			}
		}

		if (this.status === ChicagoStatus.CANCELLED) {
			this.canCancel = false;
		}
	}

	setConfirm(): void {
		if (
			!this.status ||
			this.status === 0 ||
			this.status === ChicagoStatus.NOT_SUBMITTED
		) {
			this.dialogRef.close(true);

			return;
		}

		this.confirm = true;
	}

	async cancel(form: NgForm) {
		this.submitted = true;

		if (!this.cancelCode || this.cancelCode === 0) {
			return;
		}

		if (form.valid) {
			this.loading = true;
			try {
				const response = await this.chicagoFunction
					.cancel(
						this.titleInsurance.thirdPartyId as number,
						this.cancelCode,
						this.cancelText,
					)
					.pipe(take(1))
					.toPromise();

				if (response.ResponseMessage === 'Success') {
					this.dialogRef.close(true);
				} else {
					this.dialogRef.close(false);
				}
			} catch (err) {
				LogRocket.error('Unable to get chicago document', err);
				this.dialogRef.close(false);
			} finally {
				this.loading = false;
			}
		}
	}

	close() {
		this.dialogRef.close();
	}

	selectCode($event) {
		this.cancelCode = $event.result.value;
		this.cancelValue = $event.result.name;
	}
}
